import React, {FC, useState} from "react"

import {FormattedMessage} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import {H3, Body, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import FilterChipToggle from "@irati/ui/src/FilterChipToggle"
import adminTracking from "@irati-tracking/admin"

import {EnableSSOImage} from "../../../../../images/index"
import {InfoCard} from "../../styled"
import EnableOauth from "./EnableOAuth"
import EnableSAML from "./EnableSAML"
import Header from "./Header"

const SSO_CONFIRM_CONNECTION = "SSO_confirmConnectionType"
const SSO_GET_STARTED = "SSO_getStarted"
const SSO_OAUTH_START = "SSO_OAuthStart"
const SSO_SAML_START = "SSO_samlStart"
const UNSURE_CONNECTION = "SSO_unsureConnectionType"

interface SSOConnection {
  oauth: boolean,
  saml: boolean
}

interface Props {
  onClose: () => void
}

const EnableSSO: FC<Props> = ({onClose}) => {
  const [connection, setConnection] = useState<SSOConnection>({oauth: false, saml: false})
  const [notSure, setNotSure] = useState<boolean>(false)
  const [step, setStep] = useState<number>(0)

  const handleStart = () => {
    adminTracking.startIntegrationsSSOJourney(SSO_GET_STARTED)
    setStep(1)
  }

  const handleStartAgain = () => {
    setStep(0)
    setNotSure(false)
  }

  const handleConnection = () => {
    adminTracking.startIntegrationsSSOJourney(SSO_CONFIRM_CONNECTION)
    return connection.oauth ? setStep(2) : setStep(3)
  }

  const toggleConnection = (oauth: boolean, saml:boolean) => {
    adminTracking.startIntegrationsSSOJourney(oauth ? SSO_OAUTH_START : SSO_SAML_START)
    setConnection({oauth, saml})
  }

  const handleNotSure = () => {
    adminTracking.startIntegrationsSSOJourney(UNSURE_CONNECTION)
    setNotSure(true)
  }

  return (
    <InfoCard padding={4} marginTop={6}>
      {(() => {
        switch (step) {
          case 0:
            return (
              <Flex justifyContent="space-between" width="100%">
                <Flex direction="column">
                  <H3 marginBottom={1}>
                    <FormattedMessage id="admin.components.integration.sso.title" />
                  </H3>
                  <Body marginBottom={4}>
                    <FormattedMessage id="admin.components.integration.sso.enableSSO.desc" />
                  </Body>
                  <div>
                    <Button onClick={handleStart} primary>
                      <FormattedMessage id="admin.dashboard.integrations.card.button" />
                    </Button>
                  </div>
                </Flex>
                <EnableSSOImage />
              </Flex>
            )
          case 1:
            return (
              <Flex direction="column" width="100%">
                <Header onBack={() => !notSure ? setStep(0) : setNotSure(false)} />
                {!notSure ?
                  <Flex direction="column">
                    <H3 marginBottom={1}>
                      <FormattedMessage id="admin.components.integration.sso.enableSSO.start.title" />
                    </H3>
                    <Body marginBottom={4}>
                      <FormattedMessage id="admin.components.integration.sso.title" />
                    </Body>
                    <Flex marginBottom={4}>
                      <FilterChipToggle isToggled={connection.oauth} rounded onClick={() => toggleConnection(true, false)} marginRight={2}>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.start.oauthBtn" />
                      </FilterChipToggle>
                      <FilterChipToggle isToggled={connection.saml} rounded onClick={() => toggleConnection(false, true)}>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.start.samlBtn" />
                      </FilterChipToggle>
                    </Flex>
                    <Flex>
                      <div>
                        <Button cta marginRight={4} onClick={handleNotSure} marginTop={2}>
                          <FormattedMessage id="admin.components.integration.sso.enableSSO.start.notSureBtn" />
                        </Button>
                      </div>
                      <Button onClick={handleConnection} primary disabled={!connection.oauth && !connection.saml}>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.start.nextBtn" />
                      </Button>
                    </Flex>
                  </Flex>
                  :
                  <Flex direction="column">
                    <H3 marginBottom={1}>
                      <FormattedMessage id="admin.components.integration.sso.enableSSO.notSure.title" />
                    </H3>
                    <Body marginBottom={4}>
                      <FormattedMessage id="admin.components.integration.sso.enableSSO.notSure.desc" />
                    </Body>
                    <div>
                      <Button onClick={handleStartAgain} primary>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.notSure.startAgainBtn" />
                      </Button>
                    </div>
                  </Flex>
                }
              </Flex>
            )
          case 2:
            return (
              <EnableOauth onBack={() => setStep(1)} onClose={() => setStep(4)} />
            )
          case 3:
            return (
              <EnableSAML onBack={() => setStep(1)} />
            )
          case 4:
            return (
              <Flex width="100%" justifyContent="space-between">
                <Flex direction="column">
                  <Label marginBottom={2}>
                    <FormattedMessage id="admin.components.integration.sso.title" />
                  </Label>
                  <H3 marginBottom={1}>
                    <FormattedMessage id="admin.components.integration.sso.enableSSO.oauth.done.title" />
                  </H3>
                  <Body marginBottom={4}>
                    <FormattedMessage id="admin.components.integration.sso.enableSSO.oauth.done.desc" />
                  </Body>
                  <div>
                    <Button primary onClick={onClose}>
                      <FormattedMessage id="admin.components.integration.sso.enableSSO.oauth.done.btn" />
                    </Button>
                  </div>
                </Flex>
                <EnableSSOImage />
              </Flex>
            )
          default:
            return null
        }
      })()}
    </InfoCard>
  )
}

export default EnableSSO
